import type {
  configurablePermissionTypes,
  UserPermissionType,
} from '@fv/models'

export type PermissionCategory = 'audit' | 'quote' | 'book' | 'manage'
export type ConfigurablePermission =
  (typeof configurablePermissionTypes)[number]

export type PdMapConfig = {
  label: string
  shortLabel?: string
  hint?: string
  categories: PermissionCategory[]
}
export const permissionDisplayMap: Record<ConfigurablePermission, PdMapConfig> =
  {
    'audit-viewer': {
      label: 'Audit shipments',
      hint: 'Gives access to invoices and auditing tools',
      categories: ['audit'],
    },
    'load-creator': {
      label: 'Run quotes',
      hint: 'Gives access to receive quotes',
      categories: ['quote', 'book'],
    },
    'load-booker': {
      label: 'Book shipments',
      hint: 'Gives access to book shipments',
      categories: ['book'],
    },
    'load-file-manager': {
      label: 'Manage files',
      hint: 'Gives access to manage documents for a load',
      categories: ['quote', 'book'],
    },
    'manage-addresses': {
      label: 'Manage address book',
      shortLabel: 'Address book',
      hint: 'Gives access to manage address book',
      categories: ['quote', 'book'],
    },
    'analytics-viewer': {
      label: 'View analytics',
      hint: 'Gives access to view analytics',
      categories: ['book'],
    },
    'load-canceler': {
      label: 'Cancel loads',
      hint: 'Gives access to cancel loads',
      categories: ['book'],
    },
    'load-exporter': {
      label: 'Export data',
      hint: 'Gives access to export shipment data',
      categories: ['book'],
    },
    'manage-bill-to': {
      label: 'Manage account bill-to',
      shortLabel: 'Bill-to',
      hint: 'Gives access to manage account bill-to',
      categories: ['manage'],
    },
    'manage-branding': {
      label: 'Manage branding',
      shortLabel: 'Branding',
      hint: 'Gives access to manage account branding',
      categories: ['manage'],
    },
    'manage-carriers': {
      label: 'Manage carriers',
      shortLabel: 'Carriers',
      hint: 'Gives access to manage carriers',
      categories: ['manage'],
    },
    'manage-contracted-rates': {
      label: 'Manage contracted rates',
      shortLabel: 'Contracted rates',
      hint: 'Gives access to manage contracted rates',
      categories: ['manage'],
    },
    'manage-gl-codes': {
      label: 'Manage GL codes',
      shortLabel: 'GL codes',
      hint: 'Gives access to manage GL codes',
      categories: ['manage'],
    },
    'manage-locations': {
      label: 'Manage company locations',
      shortLabel: 'Locations',
      hint: 'Gives access to manage company locations',
      categories: ['manage'],
    },
    'manage-products': {
      label: 'Manage product catalog',
      shortLabel: 'Product catalog',
      hint: 'Gives access to manage product catalog',
      categories: ['manage'],
    },
    'manage-quote-filters': {
      label: 'Manage quote filters',
      shortLabel: 'Quote filters',
      hint: 'Gives access to manage quote filter rules',
      categories: ['manage'],
    },
    'manage-spot-contacts': {
      label: 'Manage spot quote contacts',
      shortLabel: 'Spot quote ',
      hint: 'Gives access to manage spot quote contacts',
      categories: ['manage'],
    },
    'manage-settings': {
      label: 'Manage account settings',
      shortLabel: 'Account settings',
      hint: 'Gives access to manage account settings',
      categories: ['manage'],
    },
    'manage-subscription': {
      label: 'Manage subscription',
      shortLabel: 'Subscription',
      hint: 'Gives access to manage subscription',
      categories: ['manage'],
    },
    'manage-tags': {
      label: 'Manage tags',
      shortLabel: 'Tags',
      hint: 'Gives access to manage tags',
      categories: ['manage'],
    },
    'manage-users': {
      label: 'Manage users',
      shortLabel: 'Users',
      hint: 'Gives access to manage users',
      categories: ['manage'],
    },
    'manage-vendors': {
      label: 'Manage vendors',
      shortLabel: 'Vendors',
      hint: 'Gives access to manage vendors',
      categories: ['manage'],
    },
  }

export const permissionsByCategory = Object.entries(
  permissionDisplayMap,
).reduce(
  (acc, [perm, config]) => {
    config.categories.forEach(category => {
      if (!acc[category]) {
        acc[category] = []
      }
      acc[category].push(perm as ConfigurablePermission)
    })
    return acc
  },
  {} as Record<PermissionCategory, ConfigurablePermission[]>,
)

export const normalizeConfigurablePermissions = (
  permissions: UserPermissionType[],
): ConfigurablePermission[] => {
  const result: ConfigurablePermission[] = []
  permissions?.forEach(p => {
    if (p === 'view-rates') return
    if (p === 'load-booker' && !result.includes('load-creator')) {
      result.push('load-creator')
    }
    if (!result.includes(p)) {
      result.push(p)
    }
  })
  return result
}

type PermissionDependency = {
  grants: ConfigurablePermission[]
}

const permissionDependencies: Partial<
  Record<ConfigurablePermission, PermissionDependency>
> = {
  'load-booker': {
    grants: ['load-creator'],
  },
}
const permissionsGranted = Object.values(permissionDependencies).reduce(
  (acc, dependency) => [...acc, ...dependency.grants],
  [] as ConfigurablePermission[],
)

export const processPermissionChange = (
  existing: ConfigurablePermission[],
  permission: ConfigurablePermission,
  enabled: boolean,
) => {
  if (enabled && permissionDependencies[permission]) {
    const dependency = permissionDependencies[permission]
    return [...existing, ...dependency.grants, permission]
  } else if (!enabled && permissionsGranted.includes(permission)) {
    const toRemove = Object.entries(permissionDependencies)
      .filter(([, dependency]) => dependency.grants.includes(permission))
      .map(([perm]) => perm)
    return existing.filter(p => p !== permission && !toRemove.includes(p))
  } else if (enabled) {
    return [...existing, permission]
  } else {
    return existing.filter(p => p !== permission)
  }
}
